import { TableCell, TableCellProps, Typography, TypographyProps, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../../../../modules/localization/translations"
import { PropsWithChildren } from "react"
import { tailwindPalette } from "../../../../../../modules/theme/tailwindPalette"
import { FluentVariable } from "@fluent/bundle"

const useStyles = makeStyles()((theme) => ({
    blockTitle: {
        fontWeight: 500,
        fontStyle: "normal",
        fontSize: "1.875rem",
        lineHeight: "1.33",
        letterSpacing: 0,
        color: theme.palette.text.primary,
        [theme.breakpoints.down("sm")]: {
            padding: "0 1.25rem",
        },
    },
    sectionTitle: {
        fontWeight: 500,
        fontSize: "1.25rem",
        lineHeight: "1.33",
        letterSpacing: "0.15px",
        color: theme.palette.text.primary,
    },
    sectionSubTitle: {
        fontWeight: 500,
        fontStyle: "normal",
        fontSize: "17px",
        lineHeight: "1.5",
        letterSpacing: "0.15px",
        color: theme.palette.text.primary,
    },
    sectionItemLabel: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1.5",
        letterSpacing: "0.15px",
        color: theme.palette.text.secondary,
    },
    sectionItemData: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: "1.5",
        letterSpacing: "0.15px",
        color: theme.palette.text.primary,
    },
    tableLabel: {
        fontWeight: 600,
        fontStyle: "normal",
        fontSize: "17px",
        lineHeight: "1.5",
        letterSpacing: "0.15px",
        textTransform: "uppercase",
        color: tailwindPalette.slate[500],
    },
    sectionBlock: {
        padding: "20px",
        borderRadius: "16px",
    },
    titleDataContainer: {}, // in case we need to update container
    orderAmount: {
        fontWeight: 600,
        fontStyle: "normal",
        fontSize: "2.125rem",
        lineHeight: "1.24",
        letterSpacing: "0.25px",
        color: theme.palette.text.primary,
    },
}))

type WithChildrenAndClass = PropsWithChildren<{
    classNames?: string
}>

type WithTranslation = {
    translationKey: TranslationKey
    translationVars?: Record<string, FluentVariable>
}

export const SectionTitle = ({
    children,
    translationKey,
    translationVars,
    classNames,
}: WithChildrenAndClass & WithTranslation) => {
    const { classes, cx } = useStyles()

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <Typography variant="h6" className={cx(classes.sectionTitle, classNames)}>
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

export const SectionSubTitle = ({
    children,
    classNames,
    translationKey,
    translationVars,
}: WithChildrenAndClass & Partial<WithTranslation>) => {
    const { classes, cx } = useStyles()

    if (!translationKey) {
        return (
            <Typography variant="subtitle1" className={cx(classes.sectionSubTitle, classNames)}>
                {children}
            </Typography>
        )
    }

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <Typography variant="subtitle1" className={cx(classes.sectionSubTitle, classNames)}>
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

export const BlockTitle = ({
    children,
    translationKey,
    classNames,
    translationVars,
}: WithChildrenAndClass & WithTranslation) => {
    const { classes, cx } = useStyles()

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <Typography variant="h6" className={cx(classes.blockTitle, classNames)}>
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

type SectionItemLabelProps = WithChildrenAndClass & Partial<WithTranslation>

export const SectionItemLabel = ({
    children,
    variant = "body1",
    translationKey,
    classNames,
    translationVars,
}: SectionItemLabelProps & TypographyProps) => {
    const { classes, cx } = useStyles()

    if (!translationKey) {
        return (
            <Typography variant="body1" className={cx(classes.sectionItemLabel, classNames)}>
                {children}
            </Typography>
        )
    }

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <Typography variant={variant} className={cx(classes.sectionItemLabel, classNames)}>
                {children}
            </Typography>
        </LocalizedStrict>
    )
}

export const SectionItemData = ({
    children,
    classNames,
    variant = "body1",
    ...rest
}: WithChildrenAndClass & TypographyProps) => {
    const { classes, cx } = useStyles()

    return (
        <Typography variant={variant} className={cx(classes.sectionItemData, classNames)} {...rest}>
            {children}
        </Typography>
    )
}

export const SectionTitleDataContainer = ({ classNames, children }: WithChildrenAndClass) => {
    const { classes, cx } = useStyles()

    return (
        <Stack
            flexDirection="row"
            flexWrap="nowrap"
            justifyContent="space-between"
            className={cx(classes.titleDataContainer, classNames)}
        >
            {children}
        </Stack>
    )
}

type TableLabelProps = TableCellProps & WithChildrenAndClass & WithTranslation

export const TableColumnLabel = ({
    children,
    translationKey,
    classNames,
    translationVars,
    ...rest
}: TableLabelProps) => {
    const { classes, cx } = useStyles()

    return (
        <LocalizedStrict id={translationKey} vars={translationVars}>
            <TableCell {...rest} className={cx(classes.tableLabel, classNames)}>
                {children}
            </TableCell>
        </LocalizedStrict>
    )
}

type SectionBlockProps = WithChildrenAndClass & {
    gap?: number
    title: string
} & WithTranslation

export const SectionBlock = ({ classNames, gap = 2, title, children, translationKey }: SectionBlockProps) => {
    const { classes, cx } = useStyles()

    return (
        <Stack gap={gap} className={cx(classes.sectionBlock, classNames)}>
            <SectionTitle translationKey={translationKey}>{title}</SectionTitle>
            {children}
        </Stack>
    )
}

export const OrderAmount = ({ children, classNames }: WithChildrenAndClass) => {
    const { classes, cx } = useStyles()

    return (
        <Typography variant="h4" className={cx(classes.orderAmount, classNames)}>
            {children}
        </Typography>
    )
}
